// People Operations → Document Compliance (compliance program, spec §14+§16).
// HR's operational view: workforce-wide document compliance + the verification
// queue — never open-every-profile. Backed by bulk endpoints (no N+1).
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FileCheck2, Loader2, ShieldAlert, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/features/shared/components/ui/dialog';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { useHasAnyPermission, useHasTenantWidePermission } from '@/rbac/useRbac';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  getDocumentComplianceSummary, getPendingDocumentVerifications,
  verifyDocument, rejectDocument, getDocuments,
} from '@/lib/hrApi';

/** Who may see the workforce-wide compliance tab (the page's original route gate). */
export const DOCUMENT_COMPLIANCE_PERMS = ['user:read:own-tenant', 'department:write', 'leavepolicy:write', 'tenant:update:own'];
/** The verification queue's permission — honoured only at TENANT scope (employee-service
 *  _require_verifier). A holder of just this reaches the page, verification tab only. */
export const DOCUMENT_VERIFY_PERMISSION = 'employee:change:approve';

type Tab = 'compliance' | 'verification';
const parseTab = (v: string | null): Tab => (v === 'verification' ? 'verification' : 'compliance');

const FILTERS = ['all', 'missing', 'pending', 'rejected', 'expiring', 'expired', 'complete'] as const;

const STATUS_BADGE: Record<string, string> = {
  complete: 'bg-green-100 text-green-800',
  attention: 'bg-orange-100 dark:bg-orange-500/[0.12] text-orange-800',
  incomplete: 'bg-red-100 text-red-800',
};

export default function DocumentCompliancePage() {
  const { t } = useTranslation(['documents']);
  const navigate = useNavigate();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { isManager } = useRoleAccess();
  // The verification queue is company-wide identity evidence: tenant-wide holders
  // only (line managers hold employee:change:approve at TEAM scope).
  const canVerify = useHasTenantWidePermission(DOCUMENT_VERIFY_PERMISSION);
  // The compliance tab keeps its original gate; a verifier-only user (reached the
  // route via the tenant-wide verify grant) never sees it.
  const canSeeCompliance = useHasAnyPermission(DOCUMENT_COMPLIANCE_PERMS) && isManager;

  // The tab lives in the URL (?tab=verification) so verification notices deep-link
  // straight to the queue and switching tabs is shareable / survives reload.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: Tab = canSeeCompliance ? parseTab(searchParams.get('tab')) : 'verification';
  const setTab = (next: Tab) => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('tab', next);
      return p;
    }, { replace: true });
  };
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('all');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [queue, setQueue] = useState<any[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<any | null>(null);
  const [reason, setReason] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [summary, pending] = await Promise.all([
        canSeeCompliance ? getDocumentComplianceSummary({ filter }) : Promise.resolve(null),
        canVerify ? getPendingDocumentVerifications() : Promise.resolve([]),
      ]);
      setData(summary);
      setQueue(pending);
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('documents:compliance_load_failed', { defaultValue: 'Could not load document compliance.' })));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  const openDoc = async (row: any) => {
    try {
      const docs = await getDocuments(row.employee_id);
      const d = (docs || []).find((x: any) => x.id === row.id);
      if (d?.download_url) window.open(d.download_url, '_blank', 'noopener');
      else toast.error(t('documents:no_preview', { defaultValue: 'No preview available.' }));
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not open the document.'));
    }
  };

  const decide = async (row: any, action: 'verify' | 'reject') => {
    setBusyId(row.id);
    try {
      if (action === 'verify') await verifyDocument(row.employee_id, row.id);
      else await rejectDocument(row.employee_id, row.id, reason.trim());
      setRejecting(null); setReason('');
      toast.success(action === 'verify'
        ? t('documents:verified_toast', { defaultValue: 'Document verified.' })
        : t('documents:rejected_toast', { defaultValue: 'Document rejected — the employee has been notified.' }));
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not save the decision.'));
    } finally {
      setBusyId(null);
    }
  };

  if (!canSeeCompliance && !canVerify) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center">
        <ShieldAlert className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <h1 className="text-h3 text-charcoal">{t('documents:managers_only', { defaultValue: 'Managers & admins only' })}</h1>
      </div>
    );
  }

  const totals = data?.totals ?? {};
  const cards: Array<[string, number, string]> = [
    [t('documents:card_compliant', { defaultValue: 'Employees compliant' }), totals.compliant ?? 0, 'text-green-600'],
    [t('documents:card_incomplete', { defaultValue: 'Employees incomplete' }), totals.incomplete ?? 0, 'text-red-600'],
    [t('documents:card_missing', { defaultValue: 'Missing documents' }), totals.missing_docs ?? 0, 'text-red-600'],
    [t('documents:card_pending', { defaultValue: 'Pending verification' }), totals.pending ?? 0, 'text-orange-600'],
    [t('documents:card_expiring', { defaultValue: 'Expiring soon' }), totals.expiring ?? 0, 'text-orange-600'],
    [t('documents:card_expired', { defaultValue: 'Expired' }), totals.expired ?? 0, 'text-red-600'],
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-h2 text-charcoal flex items-center gap-2">
            <FileCheck2 className="w-7 h-7" />
            {t('documents:compliance_title', { defaultValue: 'Document Compliance' })}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('documents:compliance_sub', { defaultValue: 'Workforce-wide document status and the verification queue.' })}
          </p>
        </div>
        <Button variant="outline" onClick={() => void load()}><RefreshCw className="w-4 h-4 mr-2" />{t('documents:refresh', { defaultValue: 'Refresh' })}</Button>
      </div>

      <div className="inline-flex rounded-full bg-muted p-1">
        {(canSeeCompliance ? (['compliance', 'verification'] as const) : (['verification'] as const)).map((k) => (
          <button key={k} type="button" onClick={() => setTab(k)} aria-pressed={tab === k}
            className={`px-4 py-1.5 rounded-full text-sm font-medium ${tab === k ? 'bg-background text-foreground shadow' : 'text-muted-foreground'}`}>
            {k === 'compliance'
              ? t('documents:tab_compliance', { defaultValue: 'Compliance' })
              : `${t('documents:tab_verification', { defaultValue: 'Verification queue' })}${queue.length ? ` (${queue.length})` : ''}`}
          </button>
        ))}
      </div>

      {tab === 'compliance' ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {cards.map(([label, value, color]) => (
              <Card key={label}><CardContent className="pt-4 text-center">
                <div className={`text-2xl font-bold ${color}`}>{value}</div>
                <div className="text-xs text-muted-foreground mt-1">{label}</div>
              </CardContent></Card>
            ))}
          </div>

          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button key={f} type="button" onClick={() => setFilter(f)}
                className={`px-3 py-1 rounded-full text-xs font-medium border ${filter === f ? 'bg-foreground text-background' : 'text-muted-foreground border-border'}`}>
                {f}
              </button>
            ))}
          </div>

          <Card>
            <CardContent className="pt-4 overflow-x-auto">
              {loading ? (
                <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b border-border">
                      <th className="py-2 pr-4">{t('documents:col_employee', { defaultValue: 'Employee' })}</th>
                      <th className="py-2 pr-4">{t('documents:col_required', { defaultValue: 'Required' })}</th>
                      <th className="py-2 pr-4">{t('documents:col_uploaded', { defaultValue: 'Uploaded' })}</th>
                      <th className="py-2 pr-4">{t('documents:col_missing', { defaultValue: 'Missing' })}</th>
                      <th className="py-2 pr-4">{t('documents:col_pending', { defaultValue: 'Pending' })}</th>
                      <th className="py-2 pr-4">{t('documents:col_expiring', { defaultValue: 'Expiring' })}</th>
                      <th className="py-2">{t('documents:col_status', { defaultValue: 'Status' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.employees ?? []).map((r: any) => (
                      <tr key={r.employee_id}
                        className="border-b border-border hover:bg-muted/40 cursor-pointer"
                        onClick={() => navigate(tenantSlug ? `/t/${tenantSlug}/app/employees/${r.employee_id}` : `/app/employees/${r.employee_id}`)}>
                        <td className="py-2 pr-4 font-medium">{r.name}</td>
                        <td className="py-2 pr-4">{r.required}</td>
                        <td className="py-2 pr-4">{r.uploaded}</td>
                        <td className={`py-2 pr-4 ${r.missing ? 'text-red-600 font-semibold' : ''}`}>{r.missing}</td>
                        <td className={`py-2 pr-4 ${r.pending ? 'text-orange-600 font-semibold' : ''}`}>{r.pending}</td>
                        <td className={`py-2 pr-4 ${r.expiring || r.expired ? 'text-orange-600 font-semibold' : ''}`}>{r.expiring + r.expired}</td>
                        <td className="py-2"><Badge className={STATUS_BADGE[r.status] ?? 'bg-muted'}>{r.status}</Badge></td>
                      </tr>
                    ))}
                    {(data?.employees ?? []).length === 0 && (
                      <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">
                        {t('documents:no_rows', { defaultValue: 'No employees match this filter.' })}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardHeader><CardTitle className="text-base">{t('documents:queue_title', { defaultValue: 'Documents awaiting verification' })}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {!canVerify ? (
              <p className="text-muted-foreground">{t('documents:no_verify_perm', { defaultValue: 'Verification requires approval rights.' })}</p>
            ) : queue.length === 0 ? (
              <p className="text-muted-foreground">{t('documents:queue_empty', { defaultValue: 'Nothing awaiting verification.' })}</p>
            ) : queue.map((d) => (
              <div key={d.id} className="flex flex-wrap items-center gap-3 border border-border rounded-lg p-3">
                <div className="flex-1 min-w-[200px]">
                  <div className="font-medium">{d.employee_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {d.document_type.replace(/_/g, ' ')} · {d.file_name}
                    {d.document_number ? ` · #${d.document_number}` : ''}
                    {d.expires_at ? ` · expires ${d.expires_at}` : ''}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => void openDoc(d)}>
                  {t('documents:view', { defaultValue: 'View' })}
                </Button>
                <Button size="sm" disabled={busyId === d.id} onClick={() => void decide(d, 'verify')}>
                  <CheckCircle2 className="w-4 h-4 mr-1" />{t('documents:verify', { defaultValue: 'Verify' })}
                </Button>
                <Button size="sm" variant="destructive" onClick={() => { setRejecting(d); setReason(''); }}>
                  <XCircle className="w-4 h-4 mr-1" />{t('documents:reject', { defaultValue: 'Reject' })}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('documents:reject_title', { defaultValue: 'Reject document' })}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t('documents:reject_hint', { defaultValue: 'The employee sees this reason and can resubmit.' })}
          </p>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            className="w-full border border-border rounded-md p-2 bg-background text-foreground"
            placeholder={t('documents:reject_placeholder', { defaultValue: 'e.g. The photo is unreadable — please upload a clearer scan.' })} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejecting(null)}>{t('documents:cancel', { defaultValue: 'Cancel' })}</Button>
            <Button variant="destructive" disabled={reason.trim().length < 3 || busyId === rejecting?.id}
              onClick={() => rejecting && void decide(rejecting, 'reject')}>
              {t('documents:confirm_reject', { defaultValue: 'Reject' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
